/**
 * CustomerOrderDetail — live OData fetch for one sales order.
 *
 * Hits /api/customers/orders/:orderNumber which goes straight to Dynamics —
 * no Supabase cache. Renders the header (full ~250-field record) and line
 * items if present.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader, Package } from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Shopify.css';

const moneyFmt = (v) => {
  if (v == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'SAR', maximumFractionDigits: 2,
    }).format(Number(v));
  } catch { return `SAR ${Number(v).toLocaleString()}`; }
};
const datetimeFmt = (iso) => {
  if (!iso || String(iso).startsWith('1900-01-01')) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Promote the keys we care most about to the top; the rest get a generic dump.
const HEADER_PRIORITY = [
  'SalesOrderNumber', 'InvoiceCustomerAccountNumber', 'SalesOrderName',
  'SalesOrderStatus', 'SalesOrderProcessingStatus',
  'TotalOrderAmount', 'ARF_Balance', 'ARF_Received',
  'ARF_SubTotal', 'ARF_TotalTax', 'ARF_TotalDisc',
  'OrderCreationDateTime', 'RequestedReceiptDate', 'ConfirmedShippingDate', 'BA_DispatchDate',
  'ARF_Branch', 'ARF_DestinationCode', 'ARF_CustomerType', 'ARF_CustPhone',
  'DeliveryAddressCity', 'DeliveryAddressDistrictName', 'FormattedDelveryAddress',
  'SalesOrderPoolId', 'SalesOrderOriginCode',
  'OrderTakerPersonnelNumber', 'OrderResponsiblePersonnelNumber',
];

const isEmpty = (v) => v == null || v === '' || v === 0 || v === 'No' || v === 'None'
  || (typeof v === 'string' && v.startsWith('1900-01-01'));

const formatValue = (k, v) => {
  if (isEmpty(v)) return '—';
  if (typeof v === 'string' && /DateTime$/.test(k)) return datetimeFmt(v);
  if (/Date$/.test(k)) return datetimeFmt(v);
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
};

const CustomerOrderDetail = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();

  const [header, setHeader] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/api/customers/orders/${encodeURIComponent(orderNumber)}`)
      .then(r => {
        if (!active) return;
        setHeader(r.data.header);
        setLines(r.data.lines || []);
      })
      .catch(e => { if (active) showError('Failed to fetch order from Dynamics: ' + (e.response?.data?.error || e.message)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [orderNumber, showError]);

  if (loading) return (
    <div className="shop-loading">
      <Loader size={20} className="shop-spinner" />
      <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>
        Fetching order from Dynamics live…
      </div>
    </div>
  );
  if (!header) return <div className="shop"><div className="shop-empty">Order not found in Dynamics.</div></div>;

  // The rest of header keys (the long tail) — skip empty so we don't bury the user
  const otherKeys = Object.keys(header)
    .filter(k => !HEADER_PRIORITY.includes(k) && !k.startsWith('@odata') && !isEmpty(header[k]))
    .sort();

  return (
    <div className="shop">
      <button onClick={() => navigate(-1)} className="shop-action-btn" style={{ marginBottom: '1rem' }}>
        <ArrowLeft size={14} /> Back
      </button>

      <div className="shop-header">
        <div>
          <h1 className="shop-title">{header.SalesOrderNumber}</h1>
          <p className="shop-subtitle">
            {header.SalesOrderName || header.InvoiceCustomerAccountNumber}
            {' · '}
            <span className={`shop-badge ${(header.SalesOrderProcessingStatus || '').toLowerCase()}`} style={{ marginLeft: 4 }}>
              {header.SalesOrderStatus || header.SalesOrderProcessingStatus || '—'}
            </span>
            <span style={{ marginLeft: 8, fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>(live from Dynamics)</span>
          </p>
        </div>
      </div>

      <div className="shop-kpis">
        <div className="shop-kpi">
          <div className="shop-kpi-head"><span className="shop-kpi-icon green" /> Total</div>
          <div className="shop-kpi-value green">{moneyFmt(Number(header.TotalOrderAmount) || (Number(header.ARF_SubTotal || 0) + Number(header.ARF_TotalTax || 0)))}</div>
          <div className="shop-kpi-sub">Subtotal {moneyFmt(header.ARF_SubTotal)} + tax {moneyFmt(header.ARF_TotalTax)} − disc {moneyFmt(header.ARF_TotalDisc)}</div>
        </div>
        <div className="shop-kpi">
          <div className="shop-kpi-head"><span className="shop-kpi-icon amber" /> Outstanding balance</div>
          <div className={`shop-kpi-value ${header.ARF_Balance > 0 ? 'amber' : 'neutral'}`}>{moneyFmt(header.ARF_Balance)}</div>
          <div className="shop-kpi-sub">Paid: {moneyFmt(header.ARF_Received)}</div>
        </div>
        <div className="shop-kpi">
          <div className="shop-kpi-head"><span className="shop-kpi-icon blue" /> Branch / pool</div>
          <div className="shop-kpi-value blue">{header.ARF_Branch || '—'}</div>
          <div className="shop-kpi-sub">{header.SalesOrderPoolId || '—'} · {header.ARF_DestinationCode || '—'}</div>
        </div>
        <div className="shop-kpi">
          <div className="shop-kpi-head"><span className="shop-kpi-icon neutral" /> Sales rep</div>
          <div className="shop-kpi-value neutral">{header.OrderTakerPersonnelNumber || '—'}</div>
          <div className="shop-kpi-sub">Responsible: {header.OrderResponsiblePersonnelNumber || '—'}</div>
        </div>
      </div>

      {/* Line items */}
      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3><Package size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Line items</h3>
          <span className="shop-panel-meta">{lines.length} line{lines.length === 1 ? '' : 's'}</span>
        </div>
        {lines.length === 0 ? (
          <div className="shop-empty">No line items returned (entity may not be exposed for this tenant).</div>
        ) : (
          <table className="shop-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit price</th>
                <th>Discount</th>
                <th>Line total</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.LineNumber || i}>
                  <td>{l.ItemNumber || '—'}</td>
                  <td>{l.LineDescription || l.ProductName || '—'}</td>
                  <td>{l.OrderedSalesQuantity ?? l.Quantity ?? '—'}</td>
                  <td>{moneyFmt(l.SalesPrice ?? l.UnitPrice)}</td>
                  <td>{moneyFmt(l.LineDiscountAmount)}</td>
                  <td style={{ fontWeight: 600 }}>{moneyFmt(l.LineAmount ?? l.SalesAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* All other header fields — collapsed / scannable */}
      {otherKeys.length > 0 && (
        <div className="shop-panel">
          <div className="shop-panel-head">
            <h3>All other fields</h3>
            <span className="shop-panel-meta">{otherKeys.length} non-empty</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.6rem 1.5rem', fontSize: '0.78rem' }}>
            {otherKeys.map(k => (
              <div key={k}>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{k}</div>
                <div>{formatValue(k, header[k])}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerOrderDetail;
